import { curryN, filter, identity, join, map, mergeWith, pick, pipe, prop, reduce, xprod } from 'ramda';

/**
 * @function clsb
 * @param {Array<Object>} classNameSources
 * @param {Array<string>} classKeys
 * @returns {string}
 * @description ClassNamesBuilder. Construct a class name string by extracting class names from all given sources with
 * provided names. Intended to work with imported css modules and `classNames` props. Duplicated styles are removed.
 *
 * @example
 * // imported styles
 * const style1 = {
 *      control: 'Control__control--2Nm',
 *      input: 'Control__input--48h',
 *      icon: 'Control__icon--56qs'
 * };
 * const style2 = {
 *      control: 'DropDown__control--49v',
 *      label: 'DropDown__label--83a',
 *      container: 'DropDown__container--35jk'
 * };
 * // classNames
 * const classNames = { icon: 'Control__icon--56qs' }
 *
 * // example 1
 * clsb([style1, style2, classNames], ['control', 'input', 'label']);
 * // result:
 * 'Control__control--2Nm DropDown__control--49v Control__input--48h DropDown__label--83a'
 *
 * // example 2
 * const buildCls = clsb([style1, style2, classNames]);
 * buildCls(['control', 'icon']);
 * // result:
 * 'Control__control--2Nm DropDown__control--49v Control__icon--56qs'
 */
export const clsb = curryN(2,
    (classNameSources, classKeys) => pipe(
        xprod(classKeys),
        map(([key, source]) => prop(key, source)),
        filter(Boolean),
        join(' ')
    )(classNameSources)
);

/**
 * @function clsx
 * ClassNamesFilter. Filter out empty items from class names list and return concatenated string of CSS classes.
 * @param {Array<string | undefined>} classNames
 * @returns {string}
 */
export const clsx = classNames => classNames.filter(Boolean).join(' ');

/**
 * @function clsm
 * @param {Array<Object>} classNameSources
 * @param {Array<string>} [classKeys] if omitted - merges whole objects
 * @returns {Object}
 * @description ClassNamesMerge. Merges CSS class name strings within provided objects and returns object with picked
 * specified class names.
 *
 * @example
 * const style1 = { label: "Card__label--36S", value: "Card__value--g2x" };
 * const style2 = { label: "Badge__label--zfu", value: "Badge__value--1ky" };
 *
 * clsm([style1, style2], ['label', 'value']);
 * // result:
 * result = {
 *     label: 'Card__label--36S Badge__label--zfu',
 *     value: 'Card__value--g2x Badge__value--1ky'
 * }
 */
export const clsm = curryN(2,
    (classNameSources, classKeys) =>
        pipe(
            reduce(mergeWith((a, b) => `${a} ${b}`), {}),
            classKeys ? pick(classKeys) : identity
        )(classNameSources)
);

/**
 * @deprecated Use {@link clsx} instead.
 */
export const getClassNames = clsx;
